import { formatLocalizedString } from "common/components/localization";
import type { GlideFC } from "lib/glide-fc";
import { GlideIcon } from "sharedUI/ui/views/glide-icon/glide-icon";
import PickerOverlay from "../../deprecated/picker-overlay";
import type { HasOverlayProps, HasOverlayState } from "../../smart/has-overlay-component/has-overlay-component";
import HasOverlayComponent from "../../smart/has-overlay-component/has-overlay-component";
import type { BasePickerProps } from "../base-picker/base-picker";
import { BasePicker } from "../base-picker/base-picker";
import type { FormElementProps } from "../form-element/form-element";

interface Props extends Partial<HasOverlayProps>, FormElementProps {
    readonly items: string[];
    readonly current: number[];
    readonly onSelect?: (index: number) => void;
    readonly onPushScreen?: () => void;
}

interface State extends HasOverlayState {}

// The display-only piece of a Dropdown so we can render in storybook
export const DropdownDisplayOnly: GlideFC<React.PropsWithChildren<BasePickerProps>> = props => {
    return <BasePicker {...props} rightItem={<GlideIcon kind="stroke" icon="st-caret" iconSize={24} />} />;
};

class Dropdown extends HasOverlayComponent<Props, State> {
    public state: State = {};

    private onBoxClick = (): void => {
        if (this.props.onPushScreen !== undefined) {
            this.props.onPushScreen();
        } else {
            this.showOverlay();
        }
    };

    private onPickerClosed = (result: number | undefined): void => {
        this.hideOverlay();

        if (result !== undefined && this.props.onSelect !== undefined) {
            this.props.onSelect(result);
        }
    };

    public render(): React.ReactNode {
        const { items, current, title, renderEnvironment } = this.props;

        let portal: React.ReactNode;
        if (this.shouldRenderPortal() && renderEnvironment !== undefined) {
            portal = this.getPortal(
                <PickerOverlay
                    renderEnvironment={renderEnvironment}
                    items={items}
                    title={title ?? ""}
                    onFinish={this.onPickerClosed}
                />
            );
        }

        let label: string | undefined;
        if (current.length === 1) {
            label = items[current[0]];
        } else if (current.length >= 2 && current.length <= 4) {
            label = formatLocalizedString("twoToFourItems", [current.length.toString()]);
        } else if (current.length >= 5) {
            label = formatLocalizedString("numberOfItems", [current.length.toString()]);
        }

        return (
            <>
                <DropdownDisplayOnly {...this.props} onClick={this.onBoxClick}>
                    {label}
                </DropdownDisplayOnly>
                {portal}
            </>
        );
    }
}

export default Dropdown;
